import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useDrag } from "@use-gesture/react";
import { motion, useMotionValue, animate } from "framer-motion";
import { Archive } from "lucide-react";
import type { Thread } from "@onemileauto/shared-types";
import { useMoveThread } from "../../hooks/use-move-thread";
import { useFolders } from "../../hooks/use-folders";
import { displayParticipants, formatRelativeTime, initialFor } from "../../lib/format";

interface ThreadListItemProps {
  thread: Thread;
  mailboxId: string;
  folderSlug: string;
  selfEmail?: string;
  selected: boolean;
}

const SWIPE_ARCHIVE_THRESHOLD = -88;

/**
 * A thread-list row. Archiving has two affordances by design, not one:
 *  - a swipe gesture (@use-gesture/react + Framer Motion), the "feels
 *    premium" interaction called out in the architecture plan
 *  - a persistent, always-visible archive button (not hover-only, not
 *    swipe-only) sized for a comfortable tap target, since this app must
 *    not depend on the user discovering a gesture (per the "no
 *    gesture-dependent interactions" constraint for a non-technical,
 *    one-handed-on-a-phone user).
 */
export function ThreadListItem({ thread, mailboxId, folderSlug, selfEmail, selected }: ThreadListItemProps) {
  const x = useMotionValue(0);
  const [archiving, setArchiving] = useState(false);
  const moveThread = useMoveThread(mailboxId, folderSlug);
  const { data: foldersData } = useFolders(mailboxId);
  const archiveFolderId = foldersData?.folders.find((f) => f.slug === "archive")?.id;

  function runArchive() {
    if (!archiveFolderId || archiving) return;
    setArchiving(true);
    moveThread.mutate({ threadId: thread.id, folderId: archiveFolderId });
  }

  const bind = useDrag(
    ({ down, movement: [mx], velocity: [vx], direction: [dx], last, cancel }) => {
      if (!archiveFolderId) {
        cancel();
        return;
      }
      if (down) {
        x.set(Math.min(0, mx));
        return;
      }
      if (last) {
        const pastThreshold = mx < SWIPE_ARCHIVE_THRESHOLD;
        const flung = vx > 0.6 && dx < 0;
        if (pastThreshold || flung) {
          animate(x, -420, { duration: 0.16, ease: "easeIn" });
          runArchive();
        } else {
          animate(x, 0, { type: "spring", stiffness: 500, damping: 40 });
        }
      }
    },
    { axis: "x", filterTaps: true, pointer: { touch: true } },
  );

  const unread = thread.unread;

  return (
    <div className="relative overflow-hidden border-b border-border">
      <div className="absolute inset-y-0 right-0 flex w-24 items-center justify-center bg-rust text-parchment">
        <Archive size={20} />
      </div>
      {/*
        @use-gesture's bind() and framer-motion's motion.div both define an
        "onDrag" prop with incompatible signatures (native DOM drag event
        vs. framer's PanInfo callback) — binding the gesture to a plain div
        and nesting the animated motion.div inside (sharing the same `x`
        MotionValue for the transform) sidesteps that collision entirely.
      */}
      <div {...bind()} style={{ touchAction: "pan-y" }} className="relative">
        <motion.div style={{ x }} className="relative bg-cream">
          <Link
            to="/$threadId"
            params={{ threadId: thread.id }}
            search={(prev) => prev}
            className={`flex items-start gap-3 py-3 pl-4 pr-2 transition-colors ${
              selected ? "bg-rust-soft/40" : "hover:bg-parchment-dim"
            }`}
          >
            <div
              className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                unread ? "bg-rust text-parchment" : "bg-parchment-dim text-muted"
              }`}
              aria-hidden
            >
              {initialFor(thread.participants, selfEmail)}
            </div>

            <div className="min-w-0 flex-1 pr-11">
              <div className="flex items-center justify-between gap-2">
                <p
                  className={`truncate text-[15px] ${
                    unread ? "font-semibold text-charcoal" : "font-medium text-charcoal/90"
                  }`}
                >
                  {displayParticipants(thread.participants, selfEmail)}
                </p>
                <span className={`shrink-0 text-xs ${unread ? "font-semibold text-rust-deep" : "text-muted"}`}>
                  {formatRelativeTime(thread.lastMessageAt)}
                </span>
              </div>
              <p className={`truncate text-sm ${unread ? "font-medium text-charcoal" : "text-charcoal/80"}`}>
                {thread.subject || "(no subject)"}
              </p>
              <p className="truncate text-sm text-muted">{thread.snippet}</p>
            </div>
          </Link>

          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              runArchive();
            }}
            disabled={!archiveFolderId || archiving}
            aria-label="Archive conversation"
            className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-muted transition-colors hover:bg-parchment-dim hover:text-rust-deep disabled:opacity-40"
          >
            <Archive size={18} />
          </button>
        </motion.div>
      </div>
    </div>
  );
}
